import type { TRIReportDataType, TRITRIDataType } from '../../types/form-r.ts';
import { triToXml } from './form-r-xml-builder.ts';
import type { ChemicalBrief, ThresholdBrief, Warning } from '../../types/tri.ts';
import type { CasOrCategory, Facility, SimpleSelectable, UsageTypeID } from '../../types/db.ts';
import type { ReleaseComputed } from '../releases-calculate.ts';
import type { OffSiteLocationID, PotwID, QuestionnaireAnswers } from '../../types/questionnaires.ts';
import { questionnaireChemicalActivitiesAndUsesCreateBlank } from '../questionnaires.ts';
import {
  formRCommonReportHeadersFromYear,
  formRCommonReportHeadersFromFacility,
} from './form-r-common-report-headers.ts';
import { formRFacilityDetails } from './form-r-facility-details.ts';
import {
  ezToSection8Value,
  formRExpandSection8Values,
  type ReleasesByChemicalAndFormRLine,
  totalAmountsByChemicalComputeFromThresholds,
} from './form-r-utils.ts';
import { formRChemicalActivitiesAndUses } from './form-r-chemical-activities-and-uses.ts';
import { formRMaximumChemicalAmountCode, type Section4ProductInfo } from './form-r-maximum-chemical-amount-code.ts';
import { formRChemicalIdentification } from './form-r-chemical-identification.ts';
import { formRSection7aWasteTreatment } from './form-r-section-7a-waste-treatment.ts';
import { formRSection7bEnergyRecovery } from './form-r-section-7b-energy-recovery.ts';
import { formRSection7cRecycling } from './form-r-section-7c-recycling.ts';
import { formRSection810SourceReductionActivities } from './form-r-section-8.10-source-reduction-activities.ts';
import { formRSection89ProductionRatio } from './form-r-section-8.9-production-ratio.ts';
import { hasNonZeroSection8Quantity, type Section8Quantities } from './form-r-section-8-sums.ts';
import type { EpaApiTriFormREZ } from '../../../cross-app-shared/types/epa-api.ts';
import { type EpaApiTriFormREZForComparison, formRXmlStructureToEz } from '../epa-comparison.ts';
import { dioxinsCategory } from '../../data/tri-dioxins.ts';
import { formRCurrentYearChemicalReleaseDataBuild } from './form-r-current-year-chemical-release-data.ts';
import { Gram, Pound } from '../formulas/units.ts';
import { formRLeafLines } from './form-r-sections.ts';

export interface FormRXmlGenerated {
  xmlForPreview: string;
  xmlForDownload: string;
  warnings: Warning[];
  transferLocationsWithMissingData: Set<OffSiteLocationID | PotwID>;
  section8CurrentYearValues: Map<CasOrCategory, Section8Quantities>;
  section8PriorYearValues: Map<CasOrCategory, Section8Quantities>;
  ezForComparison: Map<CasOrCategory, EpaApiTriFormREZForComparison>;
  formAEligibleChemicals: Map<CasOrCategory, { formUsing: 'a' | 'r' }>;
}

export interface FormRData {
  facility: SimpleSelectable<Facility>;
  year: number;
  releases: ReleaseComputed[];

  // Many answers feed directly into Form R, such as transfer location details, production ratio, etc.:
  questionnaireAnswers: QuestionnaireAnswers;
  // Determines which chemicals will be included in the output:
  usageTypesByCasOrCategory: Map<CasOrCategory, Set<UsageTypeID>>;
  // Used for the name and for determining if Form A is available:
  chemicalBriefs: Map<CasOrCategory, Pick<ChemicalBrief, 'name' | 'isSpecialConcern'>>;
  // Needed for maximum chemical amount on site:
  thresholds: ThresholdBrief[];
  // Needed for Section 4 per-product-max calculation:
  section4ProductInfo: Map<CasOrCategory, Section4ProductInfo[]>;

  // Needed for computing Section 8:
  formREzPastYear: EpaApiTriFormREZ<CasOrCategory>[];
  // Used for Section 8 if we don't have a prior-year EZ for the chemical:
  section8PriorYearQuantitiesFromCalculation: Map<CasOrCategory, Section8Quantities>;
}

export function appDataToFormRXML(data: FormRData): FormRXmlGenerated {
  const warnings: Warning[] = [];

  const transferLocationsWithMissingData = new Set<OffSiteLocationID | PotwID>();
  const commonReportHeaders = {
    ...formRCommonReportHeadersFromYear(data.year),
    ...formRCommonReportHeadersFromFacility(data.facility, warnings),
  };
  const formAEligibleChemicals = new Map<CasOrCategory, { formUsing: 'a' | 'r' }>();

  const releasesByChemical: ReleasesByChemicalAndFormRLine = new Map();
  for (const group of data.releases) {
    for (const chemicalRelease of group.chemicalReleases) {
      const key = `${chemicalRelease.casOrCategory}-${group.formRLine}` as const;
      if (releasesByChemical.has(key)) {
        warnings.push({
          warningID: 'TW1000',
          type: 'internalConsistencyCheck',
          message: `Got duplicate results for ${chemicalRelease.casOrCategory} on Form R line ${group.formRLine}`,
        });
      }

      if (chemicalRelease.casOrCategory === dioxinsCategory) {
        if (chemicalRelease.amountUnits !== 'grams') {
          warnings.push({
            warningID: 'TW1117',
            type: 'internalConsistencyCheck',
            message: `Dioxins must be reported in grams. Got ${chemicalRelease.amountUnits} on Form R line ${group.formRLine}`,
          });
        }
      } else if (chemicalRelease.amountUnits !== 'pounds') {
        warnings.push({
          warningID: 'TW1118',
          type: 'internalConsistencyCheck',
          message: `${chemicalRelease.casOrCategory} must be reported in pounds. Got ${chemicalRelease.amountUnits} on Form R line ${group.formRLine}`,
        });
      }

      releasesByChemical.set(key, {
        formRLine: group.formRLine,
        casOrCategory: chemicalRelease.casOrCategory,
        amountReleased: chemicalRelease.amountReleased,
        amountUnits: chemicalRelease.amountUnits,
        transfers: chemicalRelease.transfers,
      });
    }
  }

  const totalAmountsByChemical = totalAmountsByChemicalComputeFromThresholds(data.thresholds);

  const section8CurrentYearQuantitiesByChemical = new Map<CasOrCategory, Section8Quantities>();
  const section8PriorYearQuantitiesByChemical = new Map<CasOrCategory, Section8Quantities>();
  const ezForComparison = new Map<CasOrCategory, EpaApiTriFormREZForComparison>();

  const formR: TRITRIDataType = {
    Submission: [
      {
        TRISubmissionIdentifier: null, // Presumably this is filled by TRI-MEweb
        Facility: formRFacilityDetails(data.facility, warnings),

        Report: Array.from(data.usageTypesByCasOrCategory.entries()).map(
          ([casOrCategory, usageTypeIds]): TRIReportDataType => {
            const chem = data.chemicalBriefs.get(casOrCategory);
            let chemicalName = chem?.name ?? casOrCategory;
            // Strip off any commentary that's part of category names:
            if (/^N\d{3}$/.test(casOrCategory)) {
              chemicalName = chemicalName.replace(/ \(.+$/, '');
            }

            const totalAmountUsed = totalAmountsByChemical.get(casOrCategory) ?? { pounds: 0, grams: 0 };
            let totalPoundsReleased = 0;
            for (const formRLine of formRLeafLines) {
              const release = releasesByChemical.get(`${casOrCategory}-${formRLine}`);
              if (!release) {
                continue;
              }

              if (release.amountUnits === 'pounds') {
                totalPoundsReleased += release.amountReleased;
              } else {
                totalPoundsReleased += Gram.convertTo(Pound, release.amountReleased) as number;
              }
            }

            const { OnsiteReleaseQuantity, POTWWasteQuantity, TransferLocation, section8CurrentYearQuantities } =
              formRCurrentYearChemicalReleaseDataBuild({
                chemicalName,
                casOrCategory,
                releasesByChemical,
                questionnaireAnswers: data.questionnaireAnswers,
                warnings,
                transferLocationsWithMissingData,
              });
            section8CurrentYearQuantitiesByChemical.set(casOrCategory, section8CurrentYearQuantities);
            const {
              OnsiteUICDisposalQuantity,
              OnsiteOtherDisposalQuantity,
              OffsiteUICDisposalQuantity,
              OffsiteOtherDisposalQuantity,
              OnsiteEnergyRecoveryQuantity,
              OffsiteEnergyRecoveryQuantity,
              OnsiteRecycledQuantity,
              OffsiteRecycledQuantity,
              OnsiteTreatedQuantity,
              OffsiteTreatedQuantity,
            } = section8CurrentYearQuantities;

            const ez = data.formREzPastYear.find((item) => item.cas_registry_number === casOrCategory) ?? null;
            const section8PriorYearQuantitiesFromCalculation =
              data.section8PriorYearQuantitiesFromCalculation.get(casOrCategory) ?? null;

            // Section 8.9:
            const { ProductionRatio, ProductionRatioType } = formRSection89ProductionRatio({
              chemicalName,
              casOrCategory,
              productionOrActivityRatio: data.questionnaireAnswers.productionOrActivityRatio,
              hasPriorYearData:
                !!ez ||
                (!!section8PriorYearQuantitiesFromCalculation &&
                  hasNonZeroSection8Quantity(section8PriorYearQuantitiesFromCalculation)),
              warnings,
            });

            const section8PriorYearQuantitiesUsing = {
              OnsiteUICDisposalQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OnsiteUICDisposalQuantity',
              }),
              OnsiteOtherDisposalQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OnsiteOtherDisposalQuantity',
              }),
              OffsiteUICDisposalQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OffsiteUICDisposalQuantity',
              }),
              OffsiteOtherDisposalQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OffsiteOtherDisposalQuantity',
              }),
              OffsiteEnergyRecoveryQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OffsiteEnergyRecoveryQuantity',
              }),
              OffsiteRecycledQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OffsiteRecycledQuantity',
              }),
              OffsiteTreatedQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OffsiteTreatedQuantity',
              }),
              OnsiteEnergyRecoveryQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OnsiteEnergyRecoveryQuantity',
              }),
              OnsiteRecycledQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OnsiteRecycledQuantity',
              }),
              OnsiteTreatedQuantity: ezToSection8Value({
                ez,
                section8PriorYearQuantitiesFromCalculation,
                section8CurrentYearQuantities,
                ProductionRatio,
                section: 'OnsiteTreatedQuantity',
              }),
            };
            section8PriorYearQuantitiesByChemical.set(casOrCategory, section8PriorYearQuantitiesUsing);

            const formRReport: TRIReportDataType = {
              ...commonReportHeaders,

              ReportType: {
                ReportTypeCode: 'TRI_FORM_R',
                ReportTypeName: 'Form R',
                ReportTypeCodeListIdentifier: null,
              },

              ChemicalTradeSecretIndicator: false, // The trade secret flag doesn't seem to survive the upload to TRI-MEweb, so we'll just always set it to false here.
              SubmissionSanitizedIndicator: null, // Even if we mark the submission as trade secret, we'll provide the unsanitized data to the EPA:

              ChemicalIdentification: formRChemicalIdentification({ casOrCategory, chemicalName }),

              ChemicalActivitiesAndUses: formRChemicalActivitiesAndUses(
                casOrCategory,
                usageTypeIds,
                data.questionnaireAnswers.chemicalActivitiesAndUses[casOrCategory] ??
                  questionnaireChemicalActivitiesAndUsesCreateBlank(),
                warnings,
              ),

              // Section 4:
              MaximumChemicalAmountCode: formRMaximumChemicalAmountCode(
                casOrCategory,
                totalAmountUsed,
                data.questionnaireAnswers.maxOnSiteAmount,
                data.section4ProductInfo.get(casOrCategory) ?? [],
                warnings,
              ),

              // Section 5:
              OnsiteReleaseQuantity,
              WasteRockManagedPileIndicator: null, // Optional, so just leaving blank.
              WasteRockQuantity: null, // Optional, so just leaving blank.

              // Section 6:
              POTWWasteQuantity,
              TransferLocation,

              // Section 7A:
              WasteTreatment: formRSection7aWasteTreatment({
                chemicalName,
                casOrCategory,
                wasteTreatment: data.questionnaireAnswers.wasteTreatment,
                warnings,
              }),

              // Section 7B:
              OnsiteRecoveryProcess: formRSection7bEnergyRecovery({
                chemicalName,
                casOrCategory,
                onSiteEnergyRecovery: data.questionnaireAnswers.onSiteEnergyRecovery[casOrCategory],
                warnings,
              }),

              // Section 7C:
              OnsiteRecyclingProcess: formRSection7cRecycling({
                chemicalName,
                casOrCategory,
                onSiteRecycling: data.questionnaireAnswers.onSiteRecycling[casOrCategory],
                warnings,
              }),

              // Section 8.1 through 8.9:
              SourceReductionQuantity: {
                OnsiteUICDisposalQuantity: formRExpandSection8Values(
                  OnsiteUICDisposalQuantity,
                  section8PriorYearQuantitiesUsing.OnsiteUICDisposalQuantity,
                ),
                OnsiteOtherDisposalQuantity: formRExpandSection8Values(
                  OnsiteOtherDisposalQuantity,
                  section8PriorYearQuantitiesUsing.OnsiteOtherDisposalQuantity,
                ),
                OffsiteUICDisposalQuantity: formRExpandSection8Values(
                  OffsiteUICDisposalQuantity,
                  section8PriorYearQuantitiesUsing.OffsiteUICDisposalQuantity,
                ),
                OffsiteOtherDisposalQuantity: formRExpandSection8Values(
                  OffsiteOtherDisposalQuantity,
                  section8PriorYearQuantitiesUsing.OffsiteOtherDisposalQuantity,
                ),
                OffsiteEnergyRecoveryQuantity: formRExpandSection8Values(
                  OffsiteEnergyRecoveryQuantity,
                  section8PriorYearQuantitiesUsing.OffsiteEnergyRecoveryQuantity,
                ),
                OffsiteRecycledQuantity: formRExpandSection8Values(
                  OffsiteRecycledQuantity,
                  section8PriorYearQuantitiesUsing.OffsiteRecycledQuantity,
                ),
                OffsiteTreatedQuantity: formRExpandSection8Values(
                  OffsiteTreatedQuantity,
                  section8PriorYearQuantitiesUsing.OffsiteTreatedQuantity,
                ),
                OnsiteEnergyRecoveryQuantity: formRExpandSection8Values(
                  OnsiteEnergyRecoveryQuantity,
                  section8PriorYearQuantitiesUsing.OnsiteEnergyRecoveryQuantity,
                ),
                OnsiteRecycledQuantity: formRExpandSection8Values(
                  OnsiteRecycledQuantity,
                  section8PriorYearQuantitiesUsing.OnsiteRecycledQuantity,
                ),
                OnsiteTreatedQuantity: formRExpandSection8Values(
                  OnsiteTreatedQuantity,
                  section8PriorYearQuantitiesUsing.OnsiteTreatedQuantity,
                ),

                ReleaseQuantity: { choice: 'NA', value: true }, // No one-time catastrophic releases happened this year.

                ProductionRatio,
                ProductionRatioType,
              },

              // TODO: 6. Form R - May need to add support for Section 8.8 at some point.
              // Section 8.8, covers:
              //   1. remedial actions
              //   2. catastrophic events such as earthquakes, fires, or floods
              //   3. other one-time events not associated with normal or routine production processes
              NonProductionWasteTotal: [],

              // Section 8.10:
              SourceReduction: formRSection810SourceReductionActivities({
                chemicalName,
                casOrCategory,
                sourceReductionActivities: data.questionnaireAnswers.sourceReductionActivities,
                warnings,
              }),
            };

            ezForComparison.set(
              casOrCategory,
              formRXmlStructureToEz({
                year: data.year,
                casOrCategory,
                activities: formRReport.ChemicalActivitiesAndUses!,
                maximumChemicalAmountCode: formRReport.MaximumChemicalAmountCode!,
                releasesByChemical,
                OnsiteReleaseQuantity: formRReport.OnsiteReleaseQuantity,
                POTWWasteQuantity: formRReport.POTWWasteQuantity,
                TransferLocation: formRReport.TransferLocation,
                OnsiteRecoveryProcess: formRReport.OnsiteRecoveryProcess!,
                OnsiteRecyclingProcess: formRReport.OnsiteRecyclingProcess!,
                SourceReductionQuantity: formRReport.SourceReductionQuantity!,
              }),
            );

            // Page 13 (PDF Page 21) of EPA Instructions in https://www.epa.gov/system/files/documents/2026-04/ry_2025_rfi_final_3.24.26.pdf
            // says regarding Form A eligibility:
            // - Is this chemical or chemical category defined as a chemical of special concern?
            // - Is the amount manufactured, OR processed, OR otherwise used less than or equal to 1,000,000 lb and
            //   is the annual reportable amount less than 500 lb/vear?
            const isFormAEligible =
              !chem?.isSpecialConcern &&
              data.thresholds
                .filter((t) => t.casOrCategory === casOrCategory)
                .every((t) => t.totalPoundsUsed <= 1_000_000) &&
              totalPoundsReleased < 500;
            if (isFormAEligible) {
              formAEligibleChemicals.set(casOrCategory, { formUsing: 'r' });
            }

            if (isFormAEligible && data.questionnaireAnswers.formRVsFormA[casOrCategory] === 'a') {
              formAEligibleChemicals.set(casOrCategory, { formUsing: 'a' });

              // Form A is the same as Form R except almost everything is left out:
              const formAReport: TRIReportDataType = {
                ...commonReportHeaders,

                ReportType: {
                  ReportTypeCode: 'TRI_FORM_A',
                  ReportTypeName: 'Form A',
                  ReportTypeCodeListIdentifier: null,
                },

                ChemicalTradeSecretIndicator: false, // The trade secret flag doesn't seem to survive the upload to TRI-MEweb, so we'll just always set it to false here.
                SubmissionSanitizedIndicator: null, // Even if we mark the submission as trade secret, we'll provide the unsanitized data to the EPA:

                ChemicalIdentification: formRChemicalIdentification({ casOrCategory, chemicalName }),

                ChemicalActivitiesAndUses: null,

                // Section 4:
                MaximumChemicalAmountCode: null,

                // Section 5:
                OnsiteReleaseQuantity: [],
                WasteRockManagedPileIndicator: null,
                WasteRockQuantity: null,

                // Section 6:
                POTWWasteQuantity: [],
                TransferLocation: [],

                // Section 7A:
                WasteTreatment: null,

                // Section 7B:
                OnsiteRecoveryProcess: null,

                // Section 7C:
                OnsiteRecyclingProcess: null,

                // Section 8.1 through 8.9:
                SourceReductionQuantity: null,

                NonProductionWasteTotal: [],

                // Section 8.10:
                SourceReduction: null,
              };
              return formAReport;
            } else {
              return formRReport;
            }
          },
        ),
      },
    ],
  };

  return {
    xmlForPreview: triToXml(formR, true),
    xmlForDownload: triToXml(formR, false),
    warnings,
    transferLocationsWithMissingData,
    section8CurrentYearValues: section8CurrentYearQuantitiesByChemical,
    section8PriorYearValues: section8PriorYearQuantitiesByChemical,
    ezForComparison,
    formAEligibleChemicals,
  };
}
